import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { format } from 'date-fns';
import { Upload, FileText, Eye, Save, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LegalDocumentRenderer } from '@/components/legal/LegalDocumentRenderer';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  type LegalDocumentType,
  DOCUMENT_TYPE_CONFIG,
  useCreateLegalDocument,
  useActivateLegalDocument,
} from '@/hooks/useLegalDocuments';
import { cn } from '@/lib/utils';

interface LegalDocumentEditorProps {
  documentType: LegalDocumentType;
  onSuccess?: () => void;
}

export function LegalDocumentEditor({ documentType, onSuccess }: LegalDocumentEditorProps) {
  const config = DOCUMENT_TYPE_CONFIG[documentType];
  
  const [version, setVersion] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [title, setTitle] = useState(config.defaultTitle);
  const [content, setContent] = useState('');
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');

  const createDocument = useCreateLegalDocument();
  const activateDocument = useActivateLegalDocument();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file && file.name.endsWith('.md')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        setContent(text);
        
        // Try to extract title from first heading
        const titleMatch = text.match(/^#\s+(.+)$/m);
        if (titleMatch) {
          setTitle(titleMatch[1].trim());
        }
      };
      reader.readAsText(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/markdown': ['.md'],
    },
    maxFiles: 1,
  });

  const handleSave = async (activate: boolean) => {
    if (!version.trim() || !content.trim() || !title.trim()) {
      return;
    }

    const doc = await createDocument.mutateAsync({
      document_type: documentType,
      version: version.trim(),
      title: title.trim(),
      content: content.trim(),
      effective_date: effectiveDate || null,
    });

    if (activate && doc) {
      await activateDocument.mutateAsync({
        documentId: doc.id,
        documentType,
      });
    }

    // Reset form
    setVersion('');
    setEffectiveDate('');
    setTitle(config.defaultTitle);
    setContent('');
    onSuccess?.();
  };

  const isValid = version.trim() && content.trim() && title.trim();
  const isSaving = createDocument.isPending || activateDocument.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Upload New Version
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Version and Date */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="version">Version *</Label>
            <Input
              id="version"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="e.g., 2.0"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="effectiveDate">Effective Date</Label>
            <Input
              id="effectiveDate"
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="title">Document Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Document title"
            />
          </div>
        </div>

        {/* Content Editor with Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'edit' | 'preview')}>
          <TabsList>
            <TabsTrigger value="edit" className="gap-2">
              <FileText className="h-4 w-4" />
              Edit
            </TabsTrigger>
            <TabsTrigger value="preview" className="gap-2">
              <Eye className="h-4 w-4" />
              Preview
            </TabsTrigger>
          </TabsList>

          <TabsContent value="edit" className="space-y-4 mt-4">
            {/* Dropzone */}
            <div
              {...getRootProps()}
              className={cn(
                "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
                isDragActive
                  ? "border-primary bg-primary/5"
                  : "border-muted-foreground/25 hover:border-primary/50"
              )}
            >
              <input {...getInputProps()} />
              <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              {isDragActive ? (
                <p className="text-sm text-primary">Drop the .md file here...</p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Drag and drop a .md file here, or click to browse
                </p>
              )}
            </div>

            {/* Or divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Or paste markdown
                </span>
              </div>
            </div>

            {/* Textarea */}
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Paste your markdown content here..."
              className="min-h-[400px] font-mono text-sm"
            />
          </TabsContent>

          <TabsContent value="preview" className="mt-4">
            <Card className="bg-muted/30">
              <ScrollArea className="h-[500px]">
                <div className="p-6">
                  {content ? (
                    <LegalDocumentRenderer content={content} />
                  ) : (
                    <p className="text-muted-foreground text-center py-12">
                      No content to preview. Add markdown content in the Edit tab.
                    </p>
                  )}
                </div>
              </ScrollArea>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => handleSave(false)}
            disabled={!isValid || isSaving}
          >
            <Save className="h-4 w-4 mr-2" />
            Save as Draft
          </Button>
          <Button
            onClick={() => handleSave(true)}
            disabled={!isValid || isSaving}
          >
            <Zap className="h-4 w-4 mr-2" />
            Save and Activate
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
